import React, { useState, useEffect } from 'react';
import { supplierModel } from '../../../models/supplierModel';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFileAlt, faDownload, faTrash, faBug } from '@fortawesome/free-solid-svg-icons';
import MultiFileInput from '../../../components/common/MultiFileInput';
import '../../../styles/colors.css';

function SupplierDocuments({ settings = null }) {
  const { supplier } = useSupplierAuth();
  const [formData, setFormData] = useState({
    // Document files
    uploadedFiles: {},
    
    // Additional form fields
    Tax_Certification_Number: '',
    Tax_Certificate_Expiry_Date: '',
    BBBEE_Type: '',
    BBBEE_Level: '',
    BBBEE_Expiration_Date: '',
    COIDA_Certificate_Number: '',
    COIDA_Certificate_Expiry_Date: '',
    
    // Debug modal state
    debugModalOpen: false
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Debug: Log the supplier object to see its structure
      console.log('Supplier data:', supplier);
      
      // Populate form fields with existing user data
      if (supplier) {
        // Format dates for input fields - convert various formats to YYYY-MM-DD
        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          
          // If date is already in YYYY-MM-DD format, use it
          if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            return dateStr.split('T')[0]; // Remove time component if present
          }
          
          // Try parsing MM/DD/YYYY format (e.g., "12/15/2025")
          const mmddyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (mmddyyyyMatch) {
            const [, month, day, year] = mmddyyyyMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
          
          // Try parsing DD/MM/YYYY format (e.g., "20/10/2024")
          const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (ddmmyyyyMatch) {
            const [, first, second, year] = ddmmyyyyMatch;
            // If first number > 12, it's definitely day/month/year
            if (parseInt(first) > 12) {
              return `${year}-${second.padStart(2, '0')}-${first.padStart(2, '0')}`;
            }
            // Otherwise, check if second > 12 to determine format
            if (parseInt(second) > 12) {
              return `${year}-${first.padStart(2, '0')}-${second.padStart(2, '0')}`;
            }
            // Ambiguous - assume MM/DD/YYYY for US format
            return `${year}-${first.padStart(2, '0')}-${second.padStart(2, '0')}`;
          }
          
          return dateStr; // Return as-is if format not recognized
        };

        setFormData(prev => ({ 
          ...prev,
          Tax_Certification_Number: supplier.Tax_Certification_Number || '',
          Tax_Certificate_Expiry_Date: formatDate(supplier.Tax_Certificate_Expiry_Date),
          BBBEE_Type: supplier.BBBEE_Type || '',
          BBBEE_Level: supplier.BBBEE_Level || '',
          BBBEE_Expiration_Date: formatDate(supplier.BBBEE_Expiration_Date),
          COIDA_Certificate_Number: supplier.COIDA_Certificate_Number || '',
          COIDA_Certificate_Expiry_Date: formatDate(supplier.COIDA_Certificate_Expiry_Date),
        }));
        
        console.log('Form populated with:', {
          Tax_Certification_Number: supplier.Tax_Certification_Number,
          Tax_Certificate_Expiry_Date: formatDate(supplier.Tax_Certificate_Expiry_Date),
          BBBEE_Type: supplier.BBBEE_Type,
          BBBEE_Level: supplier.BBBEE_Level,
          BBBEE_Expiration_Date: formatDate(supplier.BBBEE_Expiration_Date),
          COIDA_Certificate_Number: supplier.COIDA_Certificate_Number,
          COIDA_Certificate_Expiry_Date: formatDate(supplier.COIDA_Certificate_Expiry_Date),
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (documentType, files) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: {
        ...prev.uploadedFiles,
        [documentType]: files
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      // Prepare form data for submission
      const submitData = new FormData();
      
      // Add uploaded files
      Object.entries(formData.uploadedFiles).forEach(([documentType, files]) => {
        if (files && files.length > 0) {
          files.forEach((file, index) => {
            if (documentType === 'Copies') {
              submitData.append(`${documentType}[]`, file);
            } else {
              submitData.append(documentType, file);
            }
          });
        }
      });
      
      // Add form fields
      const formFields = [
        'Tax_Certification_Number', 'Tax_Certificate_Expiry_Date',
        'BBBEE_Type', 'BBBEE_Level', 'BBBEE_Expiration_Date',
        'COIDA_Certificate_Number', 'COIDA_Certificate_Expiry_Date'
      ];
      
      formFields.forEach(field => {
        if (formData[field]) {
          submitData.append(field, formData[field]);
        }
      });
      
      // Submit the form
      await supplierModel.submitDocuments(submitData);
      
      toast.success('Documents submitted successfully!');
      
    } catch (error) {
      console.error('Error submitting documents:', error);
      toast.error('Error submitting documents. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Document requirements based on original PHP logic
  const documentRequirements = [
    // Documents 1-12 only shown when Status === 0 (pending/new suppliers)
    { num: 1, key: 'CK', label: 'Certified Certificate of incorporation CK1' },
    { num: 2, key: 'COR', label: 'Certified Certificate of Incorporation COR 14.1' },
    { num: 3, key: 'Trust', label: 'Deed of Trust Agreement' },
    { num: 4, key: 'Incorporation', label: 'Certified Certificate of Incorporation Section 21' },
    { num: 5, key: 'Registrar', label: 'Registrar of Close Corporation & Companies' },
    { num: 6, key: 'SARS', label: 'Receiver of Revenue (SARS)' },
    { num: 7, key: 'Auditors', label: 'Auditors Confirmation Letter' },
    { num: 8, key: 'Address', label: 'Proof of Address' },
    { num: 9, key: 'Copies', label: 'Clear Certified ID Copies (not older than 3 months) of Partners', multiple: true },
    { num: 10, key: 'Banking', label: 'Proof of Banking' },
    { num: 11, key: 'VAT', label: 'VAT Registration Document' },
    { num: 12, key: 'Authority', label: 'Local Authority' }
  ];

  // Check if user is view-only (based on status)
  const isViewOnly = supplier?.supplier_user_view_only === 1 || supplier?.status === 'Approved' || supplier?.status === 'Rejected';
  
  // Check if basic documents (1-12) should be shown - only when Status === 0
  const showBasicDocuments = supplier?.Status === 0 || supplier?.status === 0;

  // Debug modal component
  const DebugModal = () => (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setFormData(prev => ({ ...prev, debugModalOpen: false }))} />
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        
        <div className="inline-block px-6 py-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faBug} className="mr-2 text-purple-600" />
              Document Debug Information
            </h3>
            <button
              onClick={() => setFormData(prev => ({ ...prev, debugModalOpen: false }))}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-6">
            {/* User Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">User Information</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify({ 
                  supplier: supplier,
                  isViewOnly: isViewOnly,
                  showBasicDocuments: showBasicDocuments
                }, null, 2)}
              </pre>
            </div>

            {/* Uploaded Files */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Uploaded Files</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(formData.uploadedFiles, null, 2)}
              </pre>
            </div>

            {/* Form Fields */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Form Fields</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify({
                  Tax_Certification_Number: formData.Tax_Certification_Number,
                  Tax_Certificate_Expiry_Date: formData.Tax_Certificate_Expiry_Date,
                  BBBEE_Type: formData.BBBEE_Type,
                  BBBEE_Level: formData.BBBEE_Level,
                  BBBEE_Expiration_Date: formData.BBBEE_Expiration_Date,
                  COIDA_Certificate_Number: formData.COIDA_Certificate_Number,
                  COIDA_Certificate_Expiry_Date: formData.COIDA_Certificate_Expiry_Date,
                }, null, 2)}
              </pre>
            </div>

            {/* Component State */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Component State</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify({ 
                  loading: loading,
                  submitting: submitting,
                }, null, 2)}
              </pre>
            </div>

            {/* Document Requirements */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Document Requirements</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(documentRequirements, null, 2)}
              </pre>
            </div>

            {/* Environment Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Environment</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify({ 
                  timestamp: new Date().toISOString(),
                  userAgent: navigator.userAgent,
                  url: window.location.href
                }, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
              <p className="mt-2 text-gray-600">
                Upload and manage required supplier documents
              </p>
            </div>
            {/* Debug Button */}
            {settings?.debug_mode === '1' && (
              <button
                onClick={() => setFormData(prev => ({ ...prev, debugModalOpen: true }))}
                className="inline-flex items-center px-4 py-2 bg-purple-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-purple-700 focus:bg-purple-700 active:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition ease-in-out duration-150"
              >
                <FontAwesomeIcon icon={faBug} className="mr-2" />
                Debug
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {isViewOnly ? (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      View Only Mode
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Your application has been {supplier?.status?.toLowerCase()}. You can view your documents but cannot make changes.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Document Requirements Table */}
              <div className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Show info when basic documents are hidden */}
                      {!showBasicDocuments && (
                        <tr className="bg-blue-50">
                          <td colSpan="2" className="px-6 py-4 text-center">
                            <div className="text-blue-700">
                              <p className="text-sm font-medium">Document Upload Status</p>
                              <p className="text-xs mt-1">Certification documents need to be kept up to date.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      {/* Documents 1-12 - Only shown when Status === 0 */}
                      {showBasicDocuments && documentRequirements.map((doc, index) => (
                        <tr key={doc.key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="w-16 px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 text-purple-600 rounded-full font-semibold text-sm">
                              {doc.num}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-gray-900">{doc.label}</h4>
                              {!isViewOnly && (
                                <MultiFileInput
                                  files={formData.uploadedFiles[doc.key] || []}
                                  onChange={(files) => handleFileUpload(doc.key, files)}
                                  multiple={doc.multiple || false}
                                  className="w-full"
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Tax Clearance Certificate Section - Always shown */}
                      <tr className="bg-gray-50">
                        <td colSpan="2" className="px-6 py-6 border-t-4 border-purple-200">
                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-gray-900">Tax Clearance Certificate</h4>
                            
                            {!isViewOnly && (
                              <MultiFileInput
                                files={formData.uploadedFiles['Tax'] || []}
                                onChange={(files) => handleFileUpload('Tax', files)}
                                multiple={false}
                                className="w-full"
                              />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Tax Certification Number
                                </label>
                                <input
                                  type="text"
                                  value={formData.Tax_Certification_Number}
                                  onChange={(e) => handleInputChange('Tax_Certification_Number', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  required
                                  disabled={isViewOnly}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Tax Certificate Expiry Date
                                </label>
                                <input
                                  type="date"
                                  value={formData.Tax_Certificate_Expiry_Date}
                                  onChange={(e) => handleInputChange('Tax_Certificate_Expiry_Date', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  required
                                  disabled={isViewOnly}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* BEE Certificate Section - Always shown */}
                      <tr className="bg-white">
                        <td colSpan="2" className="px-6 py-6">
                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-gray-900">BEE Certificate</h4>
                            
                            {!isViewOnly && (
                              <MultiFileInput
                                files={formData.uploadedFiles['BEE_Certificate'] || []}
                                onChange={(files) => handleFileUpload('BEE_Certificate', files)}
                                multiple={false}
                                className="w-full"
                              />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  BBBEE Type
                                </label>
                                <input
                                  type="text"
                                  value={formData.BBBEE_Type}
                                  onChange={(e) => handleInputChange('BBBEE_Type', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  required
                                  disabled={isViewOnly}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  BBBEE Level
                                </label>
                                <input
                                  type="text"
                                  value={formData.BBBEE_Level}
                                  onChange={(e) => handleInputChange('BBBEE_Level', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  required
                                  disabled={isViewOnly}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  BBBEE Expiration Date
                                </label>
                                <input
                                  type="date"
                                  value={formData.BBBEE_Expiration_Date}
                                  onChange={(e) => handleInputChange('BBBEE_Expiration_Date', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  required
                                  disabled={isViewOnly}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* COIDA Certificate Section - Always shown */}
                      <tr className="bg-gray-50">
                        <td colSpan="2" className="px-6 py-6">
                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-gray-900">COIDA Certificate</h4>
                            
                            {!isViewOnly && (
                              <MultiFileInput
                                files={formData.uploadedFiles['COIDA_Certificate'] || []}
                                onChange={(files) => handleFileUpload('COIDA_Certificate', files)}
                                multiple={false}
                                className="w-full"
                              />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  COIDA Certificate Number
                                </label>
                                <input
                                  type="text"
                                  value={formData.COIDA_Certificate_Number}
                                  onChange={(e) => handleInputChange('COIDA_Certificate_Number', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  disabled={isViewOnly}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  COIDA Certificate Expiry Date
                                </label>
                                <input
                                  type="date"
                                  value={formData.COIDA_Certificate_Expiry_Date}
                                  onChange={(e) => handleInputChange('COIDA_Certificate_Expiry_Date', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder-gray-400"
                                  disabled={isViewOnly}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Submit Button */}
              {!isViewOnly && (
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center px-6 py-3 bg-green-600 border border-transparent rounded-md font-semibold text-sm text-white uppercase tracking-widest hover:bg-green-700 focus:bg-green-700 active:bg-green-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition ease-in-out duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                        Submit Documents
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Debug Modal */}
      {settings?.debug_mode === '1' && formData.debugModalOpen && <DebugModal />}
    </div>
  );
}

export default SupplierDocuments;
